"use client";

import { useEffect } from "react";
import type { PriorityMessage } from "~/hooks/priority-message";
import { Text } from "../Text/Text";
import styles from "./PinnedMessageBanner.module.scss";

/** Dismissed pinned messages, kept at the top of the page to be read again. */
export function PinnedMessageBanner({
  messages,
  onOpen,
}: {
  messages: PriorityMessage[];
  onOpen(): void;
}): React.ReactNode {
  // the sticky offsets below the header are measured on resize, so tell them
  // the header just grew (and, on the way out, shrank)
  useEffect(() => {
    const remeasure = () => {
      window.dispatchEvent(new Event("resize"));
    };
    remeasure();
    return remeasure;
  }, []);

  const [first, ...rest] = messages;
  if (!first) return null;

  // one banner however many are pinned - the rest are a count, and opening it
  // pages through them all
  const label = rest.length > 0 ? "Important updates" : first.header;

  return (
    <section
      // counted by getStickyHeaderHeight, so the category bar sits below it
      className={`${styles.row} stickyHeader`}
      aria-label={label}
      onAnimationEnd={() => window.dispatchEvent(new Event("resize"))}
    >
      <div className={styles.clip}>
        <div className={styles.content}>
          <div className={styles.heading}>
            <Text
              font="accent"
              weight="semi-strong"
              color="overPrimary"
              truncate
            >
              {first.header}
            </Text>
          </div>
          {rest.length > 0 && (
            <span className={styles.count}>+{rest.length}</span>
          )}
          <button
            type="button"
            className={styles.action}
            onClick={onOpen}
            aria-label={`Read ${label}`}
          >
            {rest.length > 0 ? "View all" : "View"}
          </button>
        </div>
      </div>
    </section>
  );
}
